import React, {
  Dispatch,
  MutableRefObject,
  ChangeEvent as ReactChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { IconButton } from '~shared/components/buttons';
import { IconSearch, IconX } from '~shared/components/icons/standard';
import { useIsomorphicLayoutEffect, useResizeObserver } from '~shared/hooks';
import { normalizeKey } from '~shared/utils/normalize-keycode';

export const BUTTON_WIDTH = 36;
export const CLOSE_FIELD_TIMEOUT = 200;
export const MAX_LENGTH = 128; // Matching with CreateWorkspaceDialog maxLength

export interface QuizFilterProps {
  /** Ref of sibling element, determines max-width when open. */
  siblingRef: MutableRefObject<HTMLDivElement>;
  /** ARIA label describing the input field. */
  'aria-label': string;
  /** Optional starting query. */
  initialValue?: string;
  /** Called for each update to the field with the updated query. */
  onChange: (q: string) => void;
  /** Called when pressing Enter to close the field. */
  onConfirm?: () => void;
  /** Make the search field visible or non-visible **/
  setIsFieldVisible: Dispatch<React.SetStateAction<boolean>>;
  /** to control outside the component if the field is visible or not. **/
  isFieldVisible: boolean;
  placeholder: string;
  fieldMaxWidth: number;
  setFieldMaxWidth: Dispatch<React.SetStateAction<number>>;
}

/**
 * Displays a trigger button and a query field when toggled open. Follows a
 * similar pattern to a Disclosure component, with some extra focus and
 * event handling.
 */
export function QuizFilter({
  siblingRef,
  initialValue,
  onChange,
  onConfirm,
  isFieldVisible,
  setIsFieldVisible,
  placeholder,
  fieldMaxWidth,
  setFieldMaxWidth,
  ...props
}: QuizFilterProps) {
  const id = 'search-filter';
  const [fieldWidth, setFieldWidth] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [value, setValue] = React.useState<string>(initialValue);
  const updateFieldWidth = useCallback(() => {
    const { width } = siblingRef.current?.getBoundingClientRect() ?? {
      width: 0,
    };
    setFieldWidth(width + BUTTON_WIDTH);
  }, [siblingRef]);

  useResizeObserver({ ref: siblingRef, onResize: updateFieldWidth });
  useIsomorphicLayoutEffect(updateFieldWidth, [siblingRef]);

  const handleOpenField = useCallback(() => {
    setIsFieldVisible(true);
    setTimeout(() => setFieldMaxWidth(fieldWidth), 0);
    inputRef.current?.focus();
  }, [fieldWidth, setIsFieldVisible, setFieldMaxWidth]);

  const handleCloseField = useCallback(() => {
    buttonRef.current?.focus();
    setFieldMaxWidth(BUTTON_WIDTH);
    setTimeout(() => {
      setIsFieldVisible(false);
    }, CLOSE_FIELD_TIMEOUT);
  }, [setFieldMaxWidth, setIsFieldVisible]);

  const handleChange = useCallback(
    (event: ReactChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
      setValue(event.target.value);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      const key = normalizeKey(event);
      if (key !== 'Escape') return;

      handleCloseField();
      onConfirm?.();
    },
    [handleCloseField, onConfirm]
  );

  const fieldContainerClass = cx(
    'absolute right-0 flex rounded-2 overflow-hidden transition-max-w ease-in-out duration-200'
  );

  React.useEffect(() => {
    return () => {
      if (isFieldVisible) {
        setIsFieldVisible(false);
        setValue('');
      }
    };
  }, [isFieldVisible, setIsFieldVisible]);

  return (
    <div className="relative">
      <div
        className={fieldContainerClass}
        style={{
          width: fieldWidth + 'px',
          maxWidth: fieldMaxWidth,
          opacity: isFieldVisible ? undefined : 0,
        }}
      >
        <div className="absolute top-0 bottom-0 left-0 flex items-center py-2 pl-4 text-grey-600">
          <IconSearch />
        </div>
        <input
          {...props}
          id={id}
          ref={inputRef}
          className="w-full py-2 pr-12 pl-14 m-0 text-base leading-8 bg-grey-50 rounded-2 border-0 focus:shadow-focus-inset outline-none appearance-none"
          type="text"
          value={value}
          onChange={handleChange}
          tabIndex={!isFieldVisible ? -1 : undefined}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={MAX_LENGTH}
        />
      </div>
      <div className="relative">
        {isFieldVisible ? (
          <IconButton
            ref={buttonRef}
            appearance="ghost"
            aria-label={translate('Exit Search')}
            aria-controls={id}
            withAltBackground
            aria-expanded
            onClick={() => {
              onChange('');
              setValue('');
              handleCloseField();
            }}
          >
            <IconX />
          </IconButton>
        ) : (
          <IconButton
            ref={buttonRef}
            appearance="secondary"
            aria-label={translate('Search')}
            aria-controls={id}
            aria-expanded="false"
            onClick={() => {
              onChange('');
              setValue('');
              handleOpenField();
            }}
          >
            <IconSearch />
          </IconButton>
        )}
      </div>
    </div>
  );
}
